/* eslint-disable react-hooks/refs -- React Hook Form's `field` object carries `ref`, so the compiler rule reads every `field.*` access as a ref read. These are plain props. */
import type { ReactNode } from 'react';
import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Checkbox,
  Combobox,
  DatePicker,
  Field,
  Input,
  SegmentedRadio,
  Select,
  Switch,
  Textarea,
  type ComboboxOption,
  type SelectOption,
} from '@/design-system';
import { formatEmiratesIdInput, groupIban, normaliseUaeMobile, normalizeEmiratesId } from '@/shared/lib/identifiers';

import { MoneyInput } from '../money';

interface BaseProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: ReactNode;
  help?: ReactNode;
  optional?: boolean;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

/** Translates Zod/i18n error keys; server messages pass through unchanged. */
function useErrorText(error: { message?: string } | undefined, params?: Record<string, unknown>): string | undefined {
  const { t } = useTranslation();
  if (!error?.message) return undefined;
  return t(error.message as 'form.required', { defaultValue: error.message, ...(params ?? {}) });
}

export function TextField<T extends FieldValues>({ control, name, label, help, optional, required, disabled, className, placeholder, autoComplete, dir, maxLength, inputMode, size }: BaseProps<T> & { placeholder?: string; autoComplete?: string; dir?: 'ltr' | 'rtl' | 'auto'; maxLength?: number; inputMode?: 'numeric' | 'decimal' | 'text'; size?: 'md' | 'lg' }) {
  const { field, fieldState } = useController({ control, name });
  const error = useErrorText(fieldState.error);
  return (
    <Field label={label} help={help} optional={optional} required={required} error={error} className={className}>
      <Input {...field} value={(field.value) ?? ''} disabled={disabled} placeholder={placeholder} autoComplete={autoComplete} dir={dir ?? 'auto'} lang={dir === 'rtl' ? 'ar' : undefined} maxLength={maxLength} inputMode={inputMode} size={size} />
    </Field>
  );
}

export function TextareaField<T extends FieldValues>({ control, name, label, help, optional, required, disabled, className, rows, placeholder }: BaseProps<T> & { rows?: number; placeholder?: string }) {
  const { field, fieldState } = useController({ control, name });
  const error = useErrorText(fieldState.error);
  return (
    <Field label={label} help={help} optional={optional} required={required} error={error} className={className}>
      <Textarea {...field} value={(field.value) ?? ''} disabled={disabled} rows={rows} placeholder={placeholder} dir="auto" />
    </Field>
  );
}

export function IntegerField<T extends FieldValues>({ control, name, label, help, optional, required, disabled, className, suffix, min, max }: BaseProps<T> & { suffix?: ReactNode; min?: number; max?: number }) {
  const { field, fieldState } = useController({ control, name });
  const error = useErrorText(fieldState.error);
  const v = field.value as number | null | undefined;
  return (
    <Field label={label} help={help} optional={optional} required={required} error={error} className={className}>
      <Input
        ref={field.ref}
        name={field.name}
        onBlur={field.onBlur}
        inputMode="numeric"
        dir="ltr"
        className="[&_input]:tabular"
        endAdornment={suffix ? <span className="text-meta">{suffix}</span> : undefined}
        value={v == null ? '' : String(v)}
        disabled={disabled}
        min={min}
        max={max}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, '');
          field.onChange(digits === '' ? null : Math.trunc(Number(digits)));
        }}
      />
    </Field>
  );
}

/**
 * A percent entered as a decimal string ("4.99" = 4.99 %): LTR digits, decimal keypad, a "%" adornment. The value
 * stays a string (never a JS number); null when empty.
 */
export function PercentField<T extends FieldValues>({ control, name, label, help, optional, required, disabled, className }: BaseProps<T>) {
  const { field, fieldState } = useController({ control, name });
  const error = useErrorText(fieldState.error);
  const v = field.value as string | null | undefined;
  return (
    <Field label={label} help={help} optional={optional} required={required} error={error} className={className}>
      <Input
        ref={field.ref}
        name={field.name}
        onBlur={field.onBlur}
        inputMode="decimal"
        dir="ltr"
        autoComplete="off"
        className="[&_input]:tabular"
        endAdornment={<span aria-hidden="true">%</span>}
        value={v ?? ''}
        disabled={disabled}
        onChange={(e) => {
          const raw = e.target.value.replace(/[\s%]/g, '').replace(',', '.');
          field.onChange(raw === '' ? null : raw);
        }}
      />
    </Field>
  );
}

export function MoneyField<T extends FieldValues>({ control, name, label, help, optional, required, disabled, className, allowNegative }: BaseProps<T> & { allowNegative?: boolean }) {
  const { field, fieldState } = useController({ control, name });
  const error = useErrorText(fieldState.error, (fieldState.error as { params?: Record<string, unknown> } | undefined)?.params);
  return (
    <Field label={label} help={help} optional={optional} required={required} error={error} className={className}>
      <MoneyInput ref={field.ref} name={field.name} value={field.value} onChange={field.onChange} onBlur={field.onBlur} disabled={disabled} allowNegative={allowNegative} onInvalid={(raw) => field.onChange(raw)} />
    </Field>
  );
}

export function SelectField<T extends FieldValues>({ control, name, label, help, optional, required, disabled, className, options, placeholder }: BaseProps<T> & { options: readonly SelectOption[]; placeholder?: string }) {
  const { field, fieldState } = useController({ control, name });
  const error = useErrorText(fieldState.error);
  return (
    <Field label={label} help={help} optional={optional} required={required} error={error} className={className}>
      <Select ref={field.ref} value={(field.value) ?? ''} onValueChange={field.onChange} options={options} placeholder={placeholder} disabled={disabled} />
    </Field>
  );
}

export function ComboboxField<T extends FieldValues>({ control, name, label, help, optional, required, disabled, className, options, placeholder, onSearchChange, loading, footer, selectedLabel }: BaseProps<T> & { options: readonly ComboboxOption[]; placeholder?: string; onSearchChange?: (q: string) => void; loading?: boolean; footer?: ReactNode; selectedLabel?: string }) {
  const { field, fieldState } = useController({ control, name });
  const error = useErrorText(fieldState.error);
  return (
    <Field label={label} help={help} optional={optional} required={required} error={error} className={className}>
      <Combobox value={(field.value) ?? null} onChange={field.onChange} options={options} placeholder={placeholder} disabled={disabled} onSearchChange={onSearchChange} loading={loading} footer={footer} selectedLabel={selectedLabel} />
    </Field>
  );
}

export function DateField<T extends FieldValues>({ control, name, label, help, optional, required, disabled, className, min, max }: BaseProps<T> & { min?: string; max?: string }) {
  const { field, fieldState } = useController({ control, name });
  const error = useErrorText(fieldState.error);
  return (
    <Field label={label} help={help} optional={optional} required={required} error={error} className={className}>
      <DatePicker value={(field.value) ?? null} onChange={field.onChange} min={min} max={max} disabled={disabled} />
    </Field>
  );
}

export function CheckboxField<T extends FieldValues>({ control, name, label, help, disabled, className }: Omit<BaseProps<T>, 'optional' | 'required'>) {
  const { field } = useController({ control, name });
  const id = `cb-${name}`;
  return (
    <div className={className}>
      <div className="flex items-start gap-2">
        <Checkbox id={id} checked={Boolean(field.value)} onCheckedChange={(v) => field.onChange(v === true)} disabled={disabled} className="mt-0.5" />
        <label htmlFor={id} className="flex flex-col">
          <span className="text-body text-ink">{label}</span>
          {help ? <span className="text-meta text-ink-muted">{help}</span> : null}
        </label>
      </div>
    </div>
  );
}

export function SwitchField<T extends FieldValues>({ control, name, label, help, disabled, className }: Omit<BaseProps<T>, 'optional' | 'required'>) {
  const { field } = useController({ control, name });
  const id = `sw-${name}`;
  return (
    <div className={className}>
      <div className="flex items-center gap-2.5">
        <Switch id={id} checked={Boolean(field.value)} onCheckedChange={field.onChange} disabled={disabled} />
        <label htmlFor={id} className="text-body text-ink">
          {label}
        </label>
      </div>
      {help ? <p className="ms-10.5 text-meta text-ink-muted">{help}</p> : null}
    </div>
  );
}

export function SegmentedField<T extends FieldValues>({ control, name, label, help, className, options }: Omit<BaseProps<T>, 'optional' | 'required'> & { options: readonly { value: string; label: ReactNode }[] }) {
  const { field, fieldState } = useController({ control, name });
  const error = useErrorText(fieldState.error);
  return (
    <Field label={label} help={help} error={error} className={className}>
      <SegmentedRadio value={(field.value) ?? ''} onValueChange={field.onChange} options={options} />
    </Field>
  );
}

export function IbanField<T extends FieldValues>({ control, name, label, help, optional, required, disabled, className }: BaseProps<T>) {
  const { field, fieldState } = useController({ control, name });
  const { t } = useTranslation();
  const error = useErrorText(fieldState.error);
  const v = (field.value as string | null | undefined) ?? '';
  return (
    <Field label={label} help={help} optional={optional} required={required} error={error} className={className}>
      <Input
        ref={field.ref}
        name={field.name}
        dir="ltr"
        className="font-mono tracking-wide uppercase"
        autoComplete="off"
        placeholder={t('form.placeholders.iban')}
        value={groupIban(v)}
        disabled={disabled}
        onChange={(e) => field.onChange(e.target.value.replace(/\s/g, '').toUpperCase())}
        onBlur={field.onBlur}
      />
    </Field>
  );
}

export function PhoneField<T extends FieldValues>({ control, name, label, help, optional, required, disabled, className }: BaseProps<T>) {
  const { field, fieldState } = useController({ control, name });
  const { t } = useTranslation();
  const error = useErrorText(fieldState.error);
  return (
    <Field label={label} help={help} optional={optional} required={required} error={error} className={className}>
      <Input
        ref={field.ref}
        name={field.name}
        dir="ltr"
        inputMode="tel"
        autoComplete="off"
        placeholder={t('form.placeholders.mobile')}
        className="[&_input]:tabular"
        value={(field.value) ?? ''}
        disabled={disabled}
        onChange={(e) => field.onChange(e.target.value)}
        onBlur={() => {
          const n = normaliseUaeMobile(field.value ?? '');
          if (n) field.onChange(n);
          field.onBlur();
        }}
      />
    </Field>
  );
}

/**
 * Emirates ID input: typed or pasted with or without separators / spaces, shown grouped as 784-YYYY-NNNNNNN-C while
 * typing (LTR). Validation (structure + check digit, same as the API) comes from zEmiratesId().
 */
export function EmiratesIdField<T extends FieldValues>({ control, name, label, help, optional, required, disabled, className }: BaseProps<T>) {
  const { field, fieldState } = useController({ control, name });
  const error = useErrorText(fieldState.error);
  return (
    <Field label={label} help={help} optional={optional} required={required} error={error} className={className}>
      <Input
        ref={field.ref}
        name={field.name}
        dir="ltr"
        inputMode="numeric"
        autoComplete="off"
        placeholder="784-1987-1234567-1"
        className="[&_input]:tabular"
        value={(field.value) ?? ''}
        disabled={disabled}
        onChange={(e) => field.onChange(formatEmiratesIdInput(e.target.value))}
        onPaste={(e) => {
          const pasted = e.clipboardData.getData('text');
          if (!pasted) return;
          e.preventDefault();
          field.onChange(normalizeEmiratesId(pasted) ?? formatEmiratesIdInput(pasted.trim()));
        }}
        onBlur={() => {
          const v = (field.value as string | null | undefined) ?? '';
          const n = normalizeEmiratesId(v);
          if (n && n !== v) field.onChange(n);
          field.onBlur();
        }}
      />
    </Field>
  );
}
